function getDefaultConfig() {
  return {
    name: "Simple-Widget",
  };
}
// change all references from mf to theleadai

/**
 * @type {Record<Partial<keyof import('./types/config').VideoProvidersObject>, string>}
 */
export const VIDEO_PROVIDER_URLS = {
  youtube: "https://www.youtube.com/",
  vimeo: "https://vimeo.com/",
  twitch: "https://www.twitch.tv/",
};

/**
 * For video providers whose url scheme is more complicated than a video id
 * (see: facebook requires usernames), define regexes to match instead of url
 * prefixes
 *
 * @type {Record<Partial<keyof import('./types/config').VideoProvidersObject>, RegExp>}
 */
export const VIDEO_PROVIDER_MATCHERS = {
  facebook: /https:\/\/www\.facebook\.com\/.+\/videos\/\d+\//,
};

/**
 * @type {Record<String, import('./types/config').LanguageTextValues>}
 */
export const DEFAULT_TEXT = {
  en: {
    placeholder: "Ask us anything!",
    title: "AI Live Chat",
    subtitle: "Typically replies in 5 seconds",
    reset: "Reset Conversation",
    close: "Close",
    downloadTranscript: "Download conversation transcript",
    copyMessageText: "Copy message to clipboard",
    headerDisclaimer: "",
    historyDisclaimer: "",
    footerDisclaimer: "",
    tooltip: "",
    promptPopup: "",
    humanModeStartNotification: "",
    humanModeEndNotification: "",
  },
  es: {
    placeholder: "¡Pregúntanos cualquier cosa!",
    title: "Chat en vivo con IA",
    subtitle: "Generalmente responde en 5 segundos",
    reset: "Reiniciar conversación",
    close: "Cerrar",
    downloadTranscript: "Descargar la transcripción de la conversación",
    copyMessageText: "Copiar mensaje al portapapeles",
    headerDisclaimer: "",
    historyDisclaimer: "",
    footerDisclaimer: "",
    tooltip: "",
    promptPopup: "",
    humanModeStartNotification: "",
    humanModeEndNotification: "",
  },
};

/**
 * @type {import('./types/config').ColorValues}
 */
export const DEFAULT_COLORS = {
  main: "#000000",
  botMessageBubble: "f6f8fa",
  botMessageText: "#000000",
  userMessageBubble: "#522faa",
  userMessageText: "white",
  humanAgentMessageBubble: "#a2d094",
  humanAgentMessageText: "#000000",
  systemLabelText: "#808080",
  linkRegularText: "",
  linkRegularBackground: "",
  linkHoverText: "",
  linkHoverBackground: "",
  linkActiveText: "",
  linkActiveBackground: "",
};

/**
 * @type {import('./types/config').ConfigObject}
 */
export const DEFAULT_CONFIG = {
  // default values for toggleable configs here
  autoPopUp: false,
  popUpTimer: 5,
  promptPopupTimer: 0,
  widgetType: "bubble", // 'bubble' | 'inline'
  chatHeight: "min(630px,70vh)",
  chatWidth: "365px",
  useAdvSizes: false,
  advSizes: {
    small: {
      breakPoint: 600,
      width: "100%",
      height: "100%",
    },
    medium: {
      breakPoint: 1280,
      width: "60rem",
      height: "80vh",
    },
    large: {
      breakPoint: 1920,
      width: "60rem",
      height: "80vh",
    },
  },
  useAdvOffsets: false,
  advOffsets: {
    panel: {
      xOffset: "20px",
      yOffset: "96px",
    },
  },
  hideBubbleWhenPanelOpen: false,
  websockets: false,
  resetIconUrl:
    "https://storage.googleapis.com/customer-icons/rotate-right-solid.svg",
  crossIconUrl:
    "https://storage.googleapis.com/customer-icons/mfai-fa-x-solid.svg",
  imageUrl:
    "https://storage.googleapis.com/customer-icons/comment-sharp-solid.svg",
  downloadIconUrl:
    "https://storage.googleapis.com/customer-icons/download-solid.svg",
  copyIconUrl:
    "https://storage.googleapis.com/customer-icons/clipboard-regular.svg",
  speakerIconUrl:
    "https://storage.googleapis.com/customer-icons/volume-off-solid.svg",
  profileImageUrl: "",
  stopIconUrl: "https://storage.googleapis.com/customer-icons/stop-solid.svg",
  checkIconUrl: "https://storage.googleapis.com/customer-icons/check-solid.svg",
  persistClose: false, //If widget is manually closed in session, keep widget closed until reopened manually or new session
  persistOpen: false, // If the widget is open, keep it open across page reloads
  convoDaysIdle: 120, // number of days between chat messages for an idle conversation before resetting the conversation on next message
  convoDaysMax: 180, // maximum number of days for any given conversation before resetting the conversation on next message
  removeClose: false, // remove the close icon from widget
  removeRestart: false, //remove the restart icon from widget
  showDownloadTranscript: false,
  showCopyButtons: false,
  approvedImageFormats: "", // list of extensions to display as images if received
  approvedAudioFormats: "", // list of extensions to display as audio if received
  approvedVideoFormats: "", // list of extensions to display as video if received
  videoProviders: {
    // settings objects for video providers. When enabled is true, render links
    // as inline video players
    youtube: { enabled: false },
    facebook: { enabled: false },
    vimeo: { enabled: false },
    twitch: { enabled: false },
  },
  allowInputWrap: false, // allow text input to wrap and grow vertically as needed
  colors: DEFAULT_COLORS,
  // if this were typescript this would be "bottomRight" as WidgetPositionOptions
  // to clear this error, but as-is it is valid
  widgetPosition: "bottomRight",
  allowedQueryPrefixes: "",
  directParams: "{}",
  showTtsButtons: false,
  linkRegularDecoration: "",
  linkHoverDecoration: "",
  linkActiveDecoration: "",
  defaultLanguage: "en",
  overrideLanguage: "",
  botTypingDurationSeconds: 3,
  useUrlLangOverride: false,
  titleFontSize: "14px",
  subtitleFontSize: "12px",
  messageFontSize: "max(0.8rem, 12px)",
  inputFontSize: "15px",
  headerDiscFontSize: "10px",
  headerDiscFontWeight: "normal",
  historyDiscFontSize: "14px",
  historyDiscFontWeight: "normal",
  footerDiscFontSize: "10px",
  footerDiscFontWeight: "normal",
  systemLabelFontSize: "14px",
  systemLabelFontWeight: "normal",
  tooltipFontSize: "14px",
  promptPopupFontSize: "14px",
  linkRegularFontSize: "",
  linkRegularFontWeight: "",
  linkHoverFontSize: "",
  linkHoverFontWeight: "",
  linkActiveFontSize: "",
  linkActiveFontWeight: "",
  chatContext: {},
  leadContext: {},
  stackId: "",
  userId: "",
};

export function readConfigFromWindow(window) {
  return window[getDefaultConfig().name + "Config"];
}

export const LANG_STATE_PREFIX = "magicformLangOverride";

/**
 * @typedef {import('./types/config').ConfigObject} ConfigObject
 * @param {ConfigObject} widgetConfig
 */
export function getLangOverrideKey(widgetConfig) {
  return `${LANG_STATE_PREFIX}_${widgetConfig.stackId}`;
}

export function getLangOverrideValue(widgetConfig) {
  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith(getLangOverrideKey(widgetConfig)))
      ?.split("=")[1] ?? undefined
  );
}

/**
 *
 * @param {import('./types/config').ConfigObject} config
 * @param {import('./types/config').LanguageTextKeys} key
 * @param {string} defaultText
 * @returns {string}
 */
export function getUserConfigText(config, key, defaultText) {
  /*
   if text block is present
     if the active language is in the text block
      if the active language has the provided key
        return it
   else (if the active language doesn't have that key or text is not present)
    fall back to getting it from the config
      if present, return that
      if not present, return provided default
  */

  if (config?.text) {
    // get the active language
    let lang = config.defaultLanguage;
    if (document.cookie.includes(LANG_STATE_PREFIX)) {
      lang = getLangOverrideValue(config);
    } else if (config.overrideLanguage) {
      lang = config.overrideLanguage;
    } else {
      if (navigator.language in config["text"]) {
        // our full language is in the list
        lang = navigator.language;
      } else if (navigator.language.split("-")[0] in config["text"]) {
        // our language part is in the list
        lang = navigator.language.split("-")[0];
      }
    }

    if (lang && lang in config.text && key in config.text[lang]) {
      return config.text[lang][key];
    }
  }

  // if the text object was missing or the text for the active language lacks
  // the provided key, check if that key is present from the old logic (i.e,
  // directly on the config)
  if (config[key]) {
    return config[key];
  }

  // if no value in language, no default in config, then return text given
  return defaultText;
}

export default getDefaultConfig();
